<script setup>
/**
 * Record a voice note, see what it said, and use the words (spec §8 "Voice input"). The words land
 * in the caller's box for the owner to read and fix before anything is drafted.
 *
 * Owner-started AI, so the upload is priced and confirmed first (P-5.12): after recording the
 * button reads "Turn into text · {credits}". Discard costs nothing. The confirm is drawn IN PLACE
 * (`AiConfirmInline`), because this button sits inside the task composer, which imports no overlay.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmInline from './AiConfirmInline.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useVoiceNote } from '../composables/useVoiceNote.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloI18n } from '../i18n.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['text']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();
const voice = useVoiceNote();
const price = useAiConfirm({ accountId, op: 'voice_note' });
const turning = ref(false);
const failure = ref('');

const clock = computed(() => {
  const whole = Math.floor(voice.seconds.value);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
});

const turnLabel = computed(() =>
  turning.value
    ? t('WASLO.SETUP_AI.VOICE.TURNING')
    : price.label(t('WASLO.SETUP_AI.VOICE.TURN'))
);

const turnIntoText = async () => {
  if (voice.blob.value === null || turning.value) return;
  turning.value = true;
  failure.value = '';
  try {
    const outcome = await price.confirmAndRun({
      run: credits =>
        wasloApi.transcribeVoiceNote(
          accountId.value,
          { blob: voice.blob.value, seconds: voice.seconds.value },
          undefined,
          withConfirm(credits)
        ),
    });
    if (outcome.cancelled) return;
    emit('text', outcome.result.text);
    voice.discard();
  } catch (error) {
    failure.value =
      error?.status === 422
        ? t('WASLO.SETUP_AI.VOICE.NOTHING_HEARD')
        : t('WASLO.SETUP_AI.VOICE.FAILED');
  } finally {
    turning.value = false;
  }
};
</script>

<template>
  <div
    v-if="voice.state.value !== 'unsupported'"
    class="flex flex-col gap-2"
    data-waslo-voice
  >
    <div class="flex flex-wrap items-center gap-2">
      <Button
        v-if="voice.state.value !== 'recording'"
        :label="t('WASLO.SETUP_AI.VOICE.RECORD')"
        icon="i-lucide-mic"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="props.disabled || turning"
        data-waslo-voice-record
        @click="voice.start()"
      />
      <Button
        v-else
        :label="t('WASLO.SETUP_AI.VOICE.STOP', { time: clock })"
        icon="i-lucide-square"
        color="ruby"
        size="sm"
        data-waslo-voice-stop
        @click="voice.stop()"
      />
      <template v-if="voice.state.value === 'recorded'">
        <Button
          :label="turnLabel"
          size="sm"
          :disabled="props.disabled || turning"
          data-waslo-voice-turn
          @click="turnIntoText"
        />
        <Button
          :label="t('WASLO.SETUP_AI.VOICE.DISCARD')"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="turning"
          data-waslo-voice-discard
          @click="voice.discard()"
        />
      </template>
    </div>
    <p
      v-if="voice.state.value === 'denied'"
      class="mb-0 text-sm text-n-amber-11"
      data-waslo-voice-denied
    >
      {{ t('WASLO.SETUP_AI.VOICE.DENIED') }}
    </p>
    <p
      v-if="failure"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-voice-failed
    >
      {{ failure }}
    </p>
    <AiConfirmInline :controller="price" />
  </div>
</template>
